/* lp-cart-drawer — open the cart drawer on the tap, repaint it once the cart
   has actually changed.

   PRESENTATIONAL ONLY. This file never posts to /cart. It replaced
   lp-cart-ladder.js and lp-kaching-bundle.js on 2026-09-10, which between
   them granted gifts, revoked gifts and rebuilt Kaching bundles; with those
   gone, nothing in this theme writes to a shopper's cart except the vendor's
   own quantity stepper in assets/cart.js. Spec:
   docs/superpowers/specs/2026-09-10-cart-drawer-simplification-design.md §4.

   WHAT IT DOES, in order of how often it runs:

   1. OPENS ON THE TAP. The theme only shows the drawer from its own
      product-form handler, and this store's add-to-cart buttons are owned by
      the Kaching app, so the theme's handler never runs: items landed in the
      cart and nothing visible happened until an observer noticed, one to two
      seconds later. A delegated capture-phase click on the add controls now
      opens the drawer immediately, showing whatever it already held, marks
      it pending (a 2px bar under the header) and appends a dimmed "Adding…"
      line built from ShopifyAnalytics.meta — no fetch, no guess about price.

   2. REPAINTS ONCE. refresh() fetches the section by the id the drawer
      element carries and swaps #MiniCart's innerHTML. It aborts any fetch
      still in flight first, so the LAST request always wins and a slow, old
      response can never land on top of a newer one. The previous runtime
      had four independent repaint triggers and was measured doing seven
      swaps in four seconds for one add.

   3. NOTICES APP ADDS BY WATCHING THE HEADER COUNT, not the network. Every
      add updates the header's <cart-count>, whoever made it; observing that
      subtree is completely passive and cannot break anyone else's request.
      Debounced, and skipped when the theme's own cart event has just been
      handled (a theme-native add or a quantity change), because that
      handler applies the response's `sections` payload itself — so those
      paths cost exactly one swap and no fetch.

   THE LONG SECTION ID MATTERS. `?section_id=cart-drawer` renders a
   DIFFERENT, unconfigured section. Always read data-section-id off the
   element; never hardcode it.

   THE PURE PART. pendingLineFor() takes the analytics meta and a variant id
   and returns the two strings the pending line shows. It touches no DOM, so
   scripts/cart-drawer-pending.test.mjs can pin it without a browser. */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- pure */

  /* ShopifyAnalytics.meta.product.variants[] carries {id, name,
     public_title}. `name` is "<product title> - <public_title>" for a real
     variant and just the product title (or "<title> - Default Title") for a
     single-variant product. Strip the SUFFIX by exact match rather than
     splitting on " - ", so a hyphenated product title survives. */
  const pendingLineFor = (meta, variantId) => {
    const variants =
      meta && meta.product && Array.isArray(meta.product.variants) ? meta.product.variants : null;
    if (!variants || variantId === null || variantId === undefined || variantId === '') return null;
    const wanted = String(variantId);
    const variant = variants.find((v) => v && String(v.id) === wanted);
    if (!variant || typeof variant.name !== 'string') return null;

    const publicTitle = typeof variant.public_title === 'string' ? variant.public_title.trim() : '';
    let title = variant.name.trim();
    if (publicTitle) {
      const suffix = ` - ${publicTitle}`;
      if (title.endsWith(suffix)) title = title.slice(0, -suffix.length).trim();
    }
    const isDefault = !publicTitle || publicTitle === 'Default Title';
    return { title, variant: isDefault ? '' : publicTitle };
  };

  /* The header count's wording, mirrored from sections/cart-drawer.liquid so
     a repaint from the vendor's payload and a repaint from our own fetch
     produce the same string. */
  const formatCount = (n) => {
    const count = Number(n);
    if (n === null || n === undefined || n === '' || !Number.isFinite(count)) return null;
    return `${count} ${count === 1 ? 'item' : 'items'}`;
  };

  window.lpCartDrawer = { pendingLineFor, formatCount };

  /* ------------------------------------------------------------- runtime
     The test harness evaluates this file against a stub global with no
     window.location — nothing but a browser has one — so bail here before
     wiring anything. */
  if (typeof window === 'undefined' || !window.location) return;

  const ADD_SELECTOR = [
    '[name="add"]',
    '.product-form__submit',
    'button[type="submit"][data-add-to-cart]',
    '.kaching-bundles__bar-add',
  ].join(',');
  const OPENER_SELECTOR = '[aria-controls="CartDrawer"]';
  /* The header count outside the drawer. The drawer's own count is a span
     this file writes to, so it is excluded from what we watch. */
  const COUNT_SELECTOR = 'cart-count:not(.lp-cart__count), [data-cart-count-bubble], .cart-count';

  const DEBOUNCE_MS = 150; /* an app's add can be two requests; coalesce a burst */
  const PAYLOAD_GRACE_MS = 400; /* the vendor just swapped from its own payload */
  const PENDING_FALLBACK_MS = 2500; /* a tap that adds nothing still resolves */

  const routes = () => (window.theme && window.theme.routes) || {};
  const rootUrl = () => (routes().root_url || '/').replace(/\/$/, '');
  const drawerEl = () => document.querySelector('cart-drawer');
  const hostEl = () => {
    const drawer = drawerEl();
    const id = drawer && drawer.getAttribute('data-section-id');
    return id ? document.getElementById(`MiniCart-${id}`) : null;
  };

  /* ------------------------------------------------------------- pending */

  let pendingTimer = null;

  const clearPending = () => {
    const drawer = drawerEl();
    if (drawer) drawer.removeAttribute('data-lp-cart-pending');
    document.querySelectorAll('[data-lp-cart-pending-line]').forEach((el) => el.remove());
    clearTimeout(pendingTimer);
    pendingTimer = null;
  };

  /* Built with createElement/textContent throughout: the title comes from
     page data, but nothing here should ever be interpreted as markup. */
  const buildPendingLine = (form) => {
    const idInput = form && form.querySelector('[name="id"]');
    const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    const info = pendingLineFor(meta, idInput && idInput.value);
    if (!info) return null;

    const li = document.createElement('li');
    li.className = 'lp-cart__line is-pending';
    li.setAttribute('data-lp-cart-pending-line', '');
    li.setAttribute('aria-busy', 'true');

    const media = document.createElement('span');
    media.className = 'lp-cart__line-media';
    media.setAttribute('aria-hidden', 'true');
    const og = document.querySelector('meta[property="og:image:secure_url"], meta[property="og:image"]');
    if (og && og.content) {
      const img = document.createElement('img');
      img.alt = '';
      img.src = og.content;
      media.appendChild(img);
    }

    const body = document.createElement('div');
    body.className = 'lp-cart__line-body';
    const title = document.createElement('span');
    title.className = 'lp-cart__line-title';
    title.textContent = info.title;
    body.appendChild(title);
    if (info.variant) {
      const opts = document.createElement('p');
      opts.className = 'lp-cart__line-opts';
      opts.textContent = info.variant;
      body.appendChild(opts);
    }
    const adding = document.createElement('p');
    adding.className = 'lp-cart__line-adding';
    adding.textContent = 'Adding…';
    body.appendChild(adding);

    const actions = document.createElement('div');
    actions.className = 'lp-cart__line-actions';
    const ghost = document.createElement('span');
    ghost.className = 'lp-cart__qty is-ghost';
    actions.appendChild(ghost);

    li.append(media, body, actions);
    return li;
  };

  const setPending = (form) => {
    const drawer = drawerEl();
    const host = hostEl();
    if (!drawer || !host) return;
    clearPending(); /* one pending line at a time */
    drawer.setAttribute('data-lp-cart-pending', '');

    const line = buildPendingLine(form);
    if (line) {
      let list = host.querySelector('.lp-cart__items');
      if (!list) {
        /* The cart was empty, so there is no list to append to. Replace the
           empty state with a bare scroll region; the repaint brings the real
           list and the footer. */
        const scroll = document.createElement('div');
        scroll.className = 'lp-cart__scroll';
        list = document.createElement('ul');
        list.className = 'lp-cart__items';
        list.setAttribute('role', 'list');
        scroll.appendChild(list);
        const empty = host.querySelector('.lp-cart__empty');
        if (empty) empty.replaceWith(scroll);
        else host.prepend(scroll);
      }
      list.appendChild(line);
    }

    /* A FLOOR FOR THE FAILURE CASE. The count observer resolves the pending
       state as soon as the add lands; this only fires when nothing does —
       sold out, a validation error, the shopper dismissing an app modal —
       so the drawer never sits on "Adding…" indefinitely. */
    pendingTimer = setTimeout(refresh, PENDING_FALLBACK_MS);
  };

  /* The drawer's own "N items". A span rather than the theme's <cart-count>
     element, which rewrites itself to the bare number on every update. */
  const setCount = (text, hidden) => {
    const drawer = drawerEl();
    const el = drawer && drawer.querySelector('[data-lp-cart-count]');
    if (!el || text === null) return;
    el.textContent = text;
    el.hidden = Boolean(hidden);
  };

  /* ------------------------------------------------------------- repaint */

  /* Swap #MiniCart (and the header count) from a rendered copy of the
     section, wherever it came from — our own fetch or the `sections` payload
     the vendor's cart requests carry. Skips the DOM write when nothing has
     changed, so a redundant call costs a string compare and no reflow.
     Returns false when the markup does not contain our element. */
  const applySection = (html) => {
    const host = hostEl();
    if (!host || typeof html !== 'string') return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.getElementById(host.id);
    if (!next) return false;
    if (next.innerHTML !== host.innerHTML) host.innerHTML = next.innerHTML;
    /* The count in the drawer's own header sits OUTSIDE #MiniCart (the
       header is the vendor shell), so the swap above does not reach it. */
    const nextCount = doc.querySelector('[data-lp-cart-count]');
    if (nextCount) setCount(nextCount.textContent.trim(), nextCount.hidden);
    return true;
  };

  let controller = null;

  const refresh = async () => {
    const drawer = drawerEl();
    const host = hostEl();
    if (!drawer || !host) return;

    if (controller) controller.abort();
    controller = new AbortController();
    const { signal } = controller;

    try {
      const url = `${rootUrl()}/?section_id=${encodeURIComponent(drawer.getAttribute('data-section-id'))}`;
      const text = await fetch(url, { headers: { Accept: 'text/html' }, signal }).then((r) => r.text());
      if (signal.aborted) return;

      applySection(text);
      clearPending();
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.warn('[lp-cart-drawer] refresh failed', err);
      /* A drawer stuck on "Adding…" is worse than one showing a slightly
         stale cart. */
      clearPending();
    }
  };

  /* -------------------------------------------------------- instant open */

  const openNow = (form) => {
    const drawer = drawerEl();
    if (!drawer) return;
    setPending(form);
    if (!drawer.hasAttribute('open') && typeof drawer.show === 'function') drawer.show();
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest && event.target.closest(ADD_SELECTOR);
      if (!trigger || trigger.disabled) return;
      if (trigger.closest('cart-drawer')) return;
      openNow(trigger.closest('form'));
    },
    true /* capture: an app may stop propagation on its own button */
  );

  /* -------------------------------------------------- cart-change observer */

  let lastPayloadSwap = 0;
  let debounceTimer = null;
  let lastCount = null;

  const readCount = () => {
    const el = document.querySelector(COUNT_SELECTOR);
    if (!el) return null;
    const n = parseInt((el.textContent || '').replace(/\D+/g, ''), 10);
    return Number.isFinite(n) ? n : null;
  };

  /* Observe a stable container, not the count element itself: the theme
     re-renders <cart-count> rather than mutating its text, so an observer
     bound to the element watches a node that has already been thrown away. */
  const watchCount = () => {
    const el = document.querySelector(COUNT_SELECTOR);
    const host = (el && (el.closest('header') || el.parentElement)) || null;
    if (!host || host.dataset.lpCartWatched === 'true') return;
    host.dataset.lpCartWatched = 'true';
    lastCount = readCount();
    new MutationObserver(() => {
      const next = readCount();
      if (next === null || next === lastCount) return;
      lastCount = next;
      if (performance.now() - lastPayloadSwap < PAYLOAD_GRACE_MS) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(refresh, DEBOUNCE_MS);
    }).observe(host, { childList: true, characterData: true, subtree: true });
  };

  /* The vendor's own repaint: assets/cart.js swaps #MiniCart from the
     `sections` payload of a quantity change or a theme-native add, then
     fires this. Nothing more to fetch — just resolve the pending state and
     note the time so the count observer does not repaint a second time. */
  /* The vendor's own cart requests come back with a `sections` payload and
     assets/cart.js swaps #MiniCart from it — but only through a <cart-items>
     element, which does not exist while the drawer shows the empty state,
     and an app's publish may carry no payload at all. So never assume the
     swap happened: apply the payload ourselves (a no-op if the vendor
     already did), and fetch when there is nothing to apply. */
  const onPayloadSwap = (cart) => {
    lastPayloadSwap = performance.now();
    if (cart && cart.errors) return;
    if (cart) setCount(formatCount(cart.item_count), cart.item_count === 0);
    const drawer = drawerEl();
    const sectionId = drawer && drawer.getAttribute('data-section-id');
    const html = cart && cart.sections && sectionId ? cart.sections[sectionId] : null;
    if (applySection(html)) clearPending();
    else refresh();
    watchCount();
  };
  document.addEventListener('cart:updated', (event) => onPayloadSwap(event.detail && event.detail.cart));
  /* Also subscribe to the theme's own pubsub. cart:updated is dispatched at
     the END of the vendor's handler, after a focus-trap call that has been
     seen to throw, so the DOM event alone is not a reliable signal. */
  const subscribe = () => {
    const pubsub = window.theme && window.theme.pubsub;
    if (!pubsub || typeof pubsub.subscribe !== 'function') return false;
    pubsub.subscribe('cartUpdate', (event) => onPayloadSwap(event && event.cart));
    return true;
  };
  if (!subscribe()) document.addEventListener('DOMContentLoaded', subscribe);

  /* ANY CART CONTROL OPENS THE DRAWER. The theme binds its own click handler
     only to the [aria-controls="CartDrawer"] elements that exist when the
     drawer element connects, so a control rendered later — the LP header
     bar's cart button after a theme-editor re-render, or any section the
     editor swaps in — is left unbound and its click falls through to the
     plain /cart link. This delegated handler catches those: the vendor's own
     handler runs first (it is bound on the element and calls
     preventDefault), so when the default is already prevented we leave the
     toggle to it; when nobody has handled the click, we do. Either way the
     drawer shows what it has at once and a cart changed in another tab or
     on the cart page is corrected behind it — refresh() skips the swap when
     nothing has changed. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest && event.target.closest(OPENER_SELECTOR);
    if (!opener || opener.closest('cart-drawer')) return;
    if (!event.defaultPrevented) {
      const drawer = drawerEl();
      if (drawer && typeof drawer.show === 'function') {
        event.preventDefault();
        if (!drawer.hasAttribute('open')) drawer.show(opener);
      }
    }
    refresh();
  });

  const init = () => watchCount();
  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();
})();
